import { useEffect, useRef, useState } from "react";
import moment from "moment";
import "moment/locale/si";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavBar from "app/layout/TopNavBar";
import Footer from "examples/Footer";
import EventCalendar from "examples/Calendar";
import CalendarServices from "app/services/CalendarServices";
import countries from "components/LH/HC/DateTimePlaceEntry/data/countries";
import LocationSearchTextBox from "components/LH/HC/LocationSearchTextBox";
import EventDetailsDialog from "components/LH/Calendar/EventDetailsDialog";

const getCurrentMonth = () => {
  const currentDate = new Date();
  return `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}`;
};

const formatLocalDate = (date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
);

const getAllowedMonthRange = () => {
  const currentDate = new Date();
  const start = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 3, 1);

  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
  };
};

const getMonthRange = (month) => {
  const [year, monthNumber] = month.split("-").map(Number);
  const lastDay = new Date(year, monthNumber, 0).getDate();

  return {
    from: `${month}-01 00-00`,
    to: `${month}-${String(lastDay).padStart(2, "0")} 23-59`,
  };
};

const getAdjacentMonthName = (month, offset, monthNames) => {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(year, monthNumber - 1 + offset, 1);
  return monthNames[date.getMonth()];
};

const getCalendarTitle = (dateInfo, monthNames) => {
  const calendarDate = dateInfo.start || dateInfo.date || dateInfo;
  const date = calendarDate instanceof Date
    ? calendarDate
    : new Date(calendarDate.year, calendarDate.month, calendarDate.day || 1);

  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

const getAgendaDateTitle = (dateInfo, monthNames, dayNames) => {
  const calendarDate = dateInfo.date || dateInfo;
  const isNativeDate = calendarDate instanceof Date;
  const year = isNativeDate ? calendarDate.getFullYear() : calendarDate.year;
  const monthNumber = isNativeDate ? calendarDate.getMonth() + 1 : calendarDate.month + 1;
  const day = isNativeDate ? calendarDate.getDate() : calendarDate.day;
  const dayNumber = new Date(year, monthNumber - 1, day).getDay();

  return `${dayNames[dayNumber]}, ${monthNames[monthNumber - 1]} ${day}, ${year}`;
};

const formatEventDate = (date, language) => {
  if (!date) return "";

  return moment(date, "YYYY-MM-DD")
    .locale(language === "si" ? "si" : "en")
    .format("dddd, MMMM D, YYYY");
};

const getDirection = (value, positive, negative) => (value >= 0 ? positive : negative);
const getRahuEventDetails = (event) => ({
  title: event.title,
  start: event.start,
  end: event.end,
  ...event.extendedProps,
});
const getDatePart = (value) => value?.slice(0, 10);
const getRahuEventsForDate = (selectedEvent, allEvents) => allEvents
  .map(getRahuEventDetails)
  .filter((item) => {
    const startDate = getDatePart(item.start) || item.date;
    const endDate = getDatePart(item.end) || item.date;

    return startDate <= selectedEvent.date && endDate >= selectedEvent.date;
  })
  .sort((first, second) => first.start.localeCompare(second.start));
const getRahuEventSx = (event) => (event.period === "daytime" ? {
  backgroundColor: "#fde8e7",
  color: "#8f302d",
  border: "1px solid #efb8b5",
} : {
  backgroundColor: "#e4f1ff",
  color: "#164b7a",
  border: "1px solid #9dc7ed",
});
const sriLanka = countries.find((country) => country.code === "LK") || countries[0];
const colombo = {
  location: {
    label: "Colombo, Western Province, Sri Lanka",
    place_id: "ChIJ6cc-KhBL4joRvDKzXxLXL_s",
  },
  locationGeoData: {
    lat: 6.9271,
    lng: 79.8612,
  },
  locationTimeZone: {
    dstOffset: 0,
    rawOffset: 19800,
    timeZoneId: "Asia/Colombo",
  },
};
const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    backgroundColor: "#ffffff",
  },
};

function RahuKalayaCalendar() {
  const { t, i18n } = useTranslation();
  const { getRahuKalayaEvents } = CalendarServices();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [month, setMonth] = useState(getCurrentMonth());
  const [country, setCountry] = useState(sriLanka);
  const [location, setLocation] = useState(colombo);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const getRahuKalayaEventsRef = useRef(getRahuKalayaEvents);
  const calendarMonths = t("calendar.months", { returnObjects: true });
  const calendarDays = t("calendar.days", { returnObjects: true });
  const calendarShortDays = t("calendar.shortDays", { returnObjects: true });
  const allowedMonthRange = getAllowedMonthRange();
  const previousMonthName = getAdjacentMonthName(month, -1, calendarMonths);
  const nextMonthName = getAdjacentMonthName(month, 1, calendarMonths);
  getRahuKalayaEventsRef.current = getRahuKalayaEvents;

  useEffect(() => {
    let active = true;

    const loadEvents = async () => {
      if (!location) {
        setEvents([]);
        return;
      }

      const { from, to } = getMonthRange(month);
      const { location: locationValue, locationGeoData } = location;
      const latitude = locationGeoData.lat;
      const longitude = locationGeoData.lng;
      const [year, monthNumber] = month.split("-").map(Number);

      setLoading(true);
      setError("");
      try {
        const data = await getRahuKalayaEventsRef.current({
          SubId: localStorage.getItem("UseActiveAccount") || "",
          Year: year,
          Month: monthNumber,
          DateTimeSearchFrom: from,
          DateTimeSearchTo: to,
          CountryCode: country.code,
          CountryName: country.label,
          TownName: locationValue.label,
          Longitude: longitude,
          LongitudeDecimal: longitude,
          LongitudeDirection: getDirection(longitude, "E", "W"),
          Latitude: latitude,
          LatitudeDecimal: latitude,
          LatitudeDirection: getDirection(latitude, "N", "S"),
          Location: locationValue.label,
          PlaceId: locationValue.place_id,
        });
        if (active) setEvents(data);
      } catch (requestError) {
        if (active) {
          setEvents([]);
          setError(requestError.response?.data?.message || t("rahuKalaya.loadError"));
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadEvents();
    return () => { active = false; };
  }, [country, location, month, t]);

  const handleDatesSet = ({ view }) => {
    const currentStart = view.currentStart;
    const nextMonth = `${currentStart.getFullYear()}-${String(currentStart.getMonth() + 1).padStart(2, "0")}`;
    setMonth((currentMonth) => (currentMonth === nextMonth ? currentMonth : nextMonth));
  };

  const handleCountryChange = (_, value) => {
    setCountry(value || sriLanka);
    setLocation(null);
    setSelectedEvent(null);
  };

  const handleEventClick = ({ event }) => {
    setSelectedEvent(getRahuEventDetails(event));
  };

  return (
    <DashboardLayout>
      <TopNavBar />
      <MDBox pt={3} pb={4}>
        <Grid container spacing={3} sx={{ p: 2, bgcolor: "#fff" }}>
          <Grid size={{ xs: 12 }}>
            
            <MDBox position="relative" minHeight={640}>
              <MDBox
                mb={2}
                px={2}
                py={1.5}
                sx={{ backgroundColor: "#c7d9e9", borderBottom: "1px solid #b5cadc", borderRadius: 1 }}
              >
                <MDTypography variant="h5" fontWeight="medium">
                  {t("rahuKalaya.title")}
                </MDTypography>
              </MDBox>
            <Alert severity="info" sx={{ mb: 3, maxWidth: 1080 }}>
              {t("rahuKalaya.featureBanner")}
            </Alert>
            <Grid container spacing={3} mb={3} sx={{ width: "100%", maxWidth: 1080 }}>
              <Grid size={{ xs: 12, md: 5 }}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>
                  {t("rahuKalaya.country")}
                </MDTypography>
                <Autocomplete
                  options={countries}
                  value={country}
                  sx={fieldSx}
                  onChange={handleCountryChange}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      {...props}
                    >
                      <Box
                        component="img"
                        loading="lazy"
                        src={`https://flagcdn.com/24x18/${option.code.toLowerCase()}.png`}
                        alt=""
                        sx={{
                          display: "block",
                          width: "24px !important",
                          height: "18px !important",
                          flex: "0 0 24px",
                          objectFit: "cover",
                        }}
                      />
                      {option.label}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: country?.code ? (
                          <Box component="span" sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                            <Box
                              component="img"
                              src={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
                              alt=""
                              sx={{
                                display: "block",
                                width: "24px !important",
                                height: "18px !important",
                                flex: "0 0 24px",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : null,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 7 }}>
                <MDTypography variant="button" fontWeight="medium" display="block" mb={0.75}>
                  {t("rahuKalaya.location")}
                </MDTypography>
                <LocationSearchTextBox
                  key={country.code}
                  date={`${month}-01`}
                  countryCode={country.code}
                  onLocationChnage={setLocation}
                  error={false}
                  placeholder={t("rahuKalaya.locationPlaceholder")}
                  sx={fieldSx}
                  initialInputValue={location === colombo ? "Colombo" : ""}
                />
              </Grid>
            </Grid>
            {!location ? <Alert severity="info" sx={{ mb: 2 }}>{t("rahuKalaya.selectLocation")}</Alert> : null}
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {location && !loading && !error && events.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>{t("rahuKalaya.empty")}</Alert>
            ) : null}
              <EventCalendar
                initialDate={`${month}-01`}
                validRange={allowedMonthRange}
                firstDay={1}
                events={events}
                displayEventTime={false}
                datesSet={handleDatesSet}
                eventClick={handleEventClick}
                eventContent={({ event }) => (
                  <span className="calendar-event-content">
                    <span className="calendar-event-label">
                      <span className="calendar-event-time">
                        {event.extendedProps.startTime} - {event.extendedProps.endTime}
                      </span>
                    </span>
                    {!isMobile ? <Icon className="calendar-event-info" fontSize="inherit">info_outline</Icon> : null}
                  </span>
                )}
                eventDidMount={({ event, el }) => {
                  el.setAttribute("title", `${event.title}: ${event.extendedProps.startTime} - ${event.extendedProps.endTime}`);
                }}
                enhancedToolbar
                toolbarAttribution
                toolbarContext={{
                  flagSrc: `https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`,
                  locationText: location?.location?.label || "",
                }}
                headerToolbar={{
                  left: "",
                  center: "prev title next",
                  right: "", // "dayGridMonth,listMonth",
                }}
                buttonText={{
                  prev: previousMonthName,
                  next: nextMonthName,
                  month: t("calendar.month"),
                  listMonth: t("calendar.agenda"),
                }}
                buttonIcons={{ prev: "chevron-left", next: "chevron-right" }}
                views={{
                  dayGridMonth: { titleFormat: (dateInfo) => getCalendarTitle(dateInfo, calendarMonths) },
                  listMonth: { titleFormat: (dateInfo) => getCalendarTitle(dateInfo, calendarMonths) },
                }}
                dayHeaderContent={({ date }) => calendarShortDays[date.getDay()]}
                listDayFormat={(dateInfo) => getAgendaDateTitle(dateInfo, calendarMonths, calendarDays)}
                listDaySideFormat={false}
                toolbarHint={isMobile ? t("calendar.tapForDetails") : ""}
                locales={[{
                  code: i18n.language,
                  week: { dow: 1 },
                  monthNames: calendarMonths,
                  monthNamesShort: calendarMonths,
                  dayNames: calendarDays,
                  dayNamesShort: calendarShortDays,
                }]}
                locale={i18n.language}
                height="auto"
              />
              {loading ? (
                <MDBox
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 2 }}
                >
                  <CircularProgress color="info" />
                </MDBox>
              ) : null}
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <EventDetailsDialog
        event={selectedEvent}
        events={events}
        commonFields={[
          { icon: "wb_sunny", label: t("rahuKalaya.sunRise"), value: (item) => item.sunRise },
          { icon: "wb_twilight", label: t("rahuKalaya.sunSet"), value: (item) => item.sunSet },
        ]}
        attributionUrl="https://app.lakhandahana.com/"
        contextFlagSrc={`https://flagcdn.com/24x18/${country.code.toLowerCase()}.png`}
        contextText={`${country.label} | ${location?.location?.label || ""}`}
        dateLabel={t("rahuKalaya.date")}
        formatDate={(date) => formatEventDate(date, i18n.language)}
        fields={[
          { label: t("rahuKalaya.start"), value: (item) => item.startTime },
          { label: t("rahuKalaya.end"), value: (item) => item.endTime },
        ]}
        getEventDetails={getRahuEventDetails}
        getEventIcon={(item) => (item.period === "daytime" ? "wb_sunny" : "nights_stay")}
        getEventSx={getRahuEventSx}
        getEventTitle={(item) => (item.period === "daytime"
          ? t("rahuKalaya.daytime")
          : t("rahuKalaya.nighttime"))}
        getNavigationKey={(item) => item.period}
        getRelatedEvents={getRahuEventsForDate}
        inlineFields
        onClose={() => setSelectedEvent(null)}
        onNavigate={setSelectedEvent}
        title={t("rahuKalaya.title")}
      />
    </DashboardLayout>
  );
}

export default RahuKalayaCalendar;
